import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Search, Calendar, Download, Eye, Truck, Filter,
  Package, FileText, Loader2, BarChart3, Users, Box, Scale,
  TrendingUp, ChevronLeft, ChevronRight, Pencil, ChevronDown, CheckCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const CustomerOutwardReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  
  // Summary Tab State
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFromDate, setSummaryFromDate] = useState('');
  const [summaryToDate, setSummaryToDate] = useState('');
  // 2026-02 — Category-4 + Tagged filters surfaced on the Summary tab so
  // the totals + breakdowns match the Reports modal on the per-document
  // page.
  const [summaryCategories, setSummaryCategories] = useState([]);          // distinct options
  const [summarySelectedCategories, setSummarySelectedCategories] = useState([]);
  const [summaryCategoryOpen, setSummaryCategoryOpen] = useState(false);
  const [summaryTaggedOnly, setSummaryTaggedOnly] = useState(false);
  
  // All Entries Tab State
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 25;
  
  // Detail Dialog State
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit dialog state
  const [editEntry, setEditEntry] = useState(null);         // entry currently being edited
  const [editForm, setEditForm] = useState({ transport_name: '', vehicle_number: '', driver_name: '', driver_phone: '', box_customers: {} });
  const [savingEdit, setSavingEdit] = useState(false);

  const openEditDialog = async (entryId) => {
    try {
      const resp = await apiClient.get(`/customer-outward/entry/${entryId}`);
      const e = resp.data?.entry || resp.data;
      const bc = {};
      (e.boxes || []).forEach(b => { bc[b.box_number] = b.customer || ''; });
      setEditForm({
        transport_name: e.transport_name || '',
        vehicle_number: e.vehicle_number || '',
        driver_name: e.driver_name || '',
        driver_phone: e.driver_phone || '',
        box_customers: bc,
      });
      setEditEntry(e);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load entry');
    }
  };

  const saveEdit = async () => {
    if (!editEntry) return;
    setSavingEdit(true);
    try {
      const payload = {
        transport_name: editForm.transport_name,
        vehicle_number: editForm.vehicle_number,
        driver_name: editForm.driver_name,
        driver_phone: editForm.driver_phone,
        box_customers: Object.entries(editForm.box_customers).map(([box_number, customer]) => ({ box_number, customer: customer || '' })),
      };
      await apiClient.patch(`/customer-outward/entries/${editEntry.entry_id}`, payload);
      toast.success('Entry updated');
      setEditEntry(null);
      fetchEntries();
      if (activeTab === 'summary') fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  // Set default date range to current month
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (d) => d.toISOString().split('T')[0];
    
    setSummaryFromDate(formatDate(firstDay));
    setSummaryToDate(formatDate(lastDay));
    setFromDate(formatDate(firstDay));
    setToDate(formatDate(lastDay));
  }, []);

  useEffect(() => {
    fetchCustomerList();
    fetchSummaryCategoryOptions();
  }, []);

  useEffect(() => {
    if (activeTab === 'summary' && summaryFromDate) {
      fetchSummary();
    }
  }, [activeTab, summaryFromDate, summaryToDate, summarySelectedCategories, summaryTaggedOnly]);

  useEffect(() => {
    if (activeTab === 'entries') {
      fetchEntries();
    }
  }, [activeTab, page, search, fromDate, toDate, selectedCustomer, selectedStatus]);

  const fetchCustomerList = async () => {
    try {
      const response = await apiClient.get('/outward/customer/reports/customers/list');
      setCustomers(response.data.customers || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSummaryCategoryOptions = async () => {
    try {
      const response = await apiClient.get('/outward/customer/reports/categories');
      setSummaryCategories(response.data?.categories || []);
    } catch (e) {
      setSummaryCategories([]);
    }
  };

  const toggleSummaryCategory = (cat) => {
    setSummarySelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const toggleSummarySelectAll = () => {
    const allSelected =
      summaryCategories.length > 0
      && summarySelectedCategories.length === summaryCategories.length;
    setSummarySelectedCategories(allSelected ? [] : [...summaryCategories]);
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const params = new URLSearchParams();
      if (summaryFromDate) params.append('from_date', summaryFromDate);
      if (summaryToDate) params.append('to_date', summaryToDate);
      if (summarySelectedCategories.length > 0) {
        params.append('categories', summarySelectedCategories.join(','));
      }
      if (summaryTaggedOnly) params.append('tagged_only', 'true');

      const response = await apiClient.get(`/outward/customer/reports/summary?${params}`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
      toast.error('Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchEntries = async () => {
    setEntriesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (selectedCustomer && selectedCustomer !== 'all') {
        params.append('customer', selectedCustomer);
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      
      const response = await apiClient.get(`/outward/customer/reports?${params}`);
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load entries');
    } finally {
      setEntriesLoading(false);
    }
  };

  const fetchEntryDetail = async (entryId) => {
    setDetailLoading(true);
    try {
      const response = await apiClient.get(`/outward/customer/reports/${entryId}`);
      setSelectedEntry(response.data.entry);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load entry details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (selectedCustomer && selectedCustomer !== 'all') {
        params.append('customer', selectedCustomer);
      }
      
      const response = await apiClient.get(`/outward/customer/reports/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `customer_outward_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  // 2026-05-25 — One-click download of the same detailed Gate Pass PDF
  // the gate-entry submit screen produces, straight from the Reports list.
  const handleDownloadGatePass = async (entry) => {
    try {
      const response = await apiClient.get(
        `/customer-outward/gate-pass/${entry.entry_id}/pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `GatePass_${entry.gate_pass_number || entry.entry_id}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Gate pass downloaded');
    } catch (error) {
      toast.error('Failed to download gate pass');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'bg-green-100 text-green-700',
      'dispatched': 'bg-blue-100 text-blue-700',
      'pending': 'bg-yellow-100 text-yellow-700',
      'cancelled': 'bg-red-100 text-red-700'
    };
    return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-stone-900 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-stone-600 hover:text-stone-900"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Customer Gate Outward Reports</h1>
            <p className="text-stone-600 text-sm">Detailed reports for customer dispatches</p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white border border-stone-200 max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="summary" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="entries" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            All Entries
          </TabsTrigger>
          <TabsTrigger value="customer-wise" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            Customer Wise
          </TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" className="space-y-4">
          {/* Date Filters */}
          <Card className="bg-white border-stone-200">
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">From Date</Label>
                  <Input
                    type="date"
                    value={summaryFromDate}
                    onChange={(e) => setSummaryFromDate(e.target.value)}
                    className="bg-white border-stone-300 w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">To Date</Label>
                  <Input
                    type="date"
                    value={summaryToDate}
                    onChange={(e) => setSummaryToDate(e.target.value)}
                    className="bg-white border-stone-300 w-40"
                  />
                </div>
                {/* 2026-02 — Category-4 multi-select. Same options as the
                    per-document Reports modal. Applies to every breakdown
                    card on this tab. */}
                <div className="space-y-1 relative">
                  <Label className="text-stone-600 text-sm">Category</Label>
                  <button
                    type="button"
                    onClick={() => setSummaryCategoryOpen(o => !o)}
                    className="h-9 w-[220px] bg-white border border-stone-300 rounded-md px-3 text-sm flex items-center justify-between text-stone-900 hover:bg-stone-100"
                    data-testid="summary-category-trigger"
                  >
                    <span className="truncate text-left flex-1">
                      {summarySelectedCategories.length === 0
                        ? 'All categories'
                        : summarySelectedCategories.length === summaryCategories.length
                          ? 'All categories'
                          : summarySelectedCategories.length === 1
                            ? summarySelectedCategories[0]
                            : `${summarySelectedCategories.length} selected`}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 ml-1 text-stone-600 transition-transform ${summaryCategoryOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {summaryCategoryOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setSummaryCategoryOpen(false)}
                      />
                      <div
                        className="absolute z-50 mt-1 w-[260px] bg-white border border-stone-200 rounded-md shadow-xl max-h-72 overflow-y-auto"
                        data-testid="summary-category-menu"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={toggleSummarySelectAll}
                          className="w-full text-left px-3 py-2 text-sm font-medium hover:bg-stone-100 border-b border-stone-200 flex items-center gap-2 text-amber-700"
                          data-testid="summary-category-select-all"
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={
                              summaryCategories.length > 0 &&
                              summarySelectedCategories.length === summaryCategories.length
                            }
                            className="rounded"
                          />
                          <span>Select All</span>
                          <span className="ml-auto text-xs text-stone-600">
                            {summaryCategories.length}
                          </span>
                        </button>
                        {summaryCategories.length === 0 && (
                          <div className="px-3 py-2 text-xs text-stone-500">
                            No categories found in item_master.
                          </div>
                        )}
                        {summaryCategories.map((cat) => {
                          const checked = summarySelectedCategories.includes(cat);
                          return (
                            <label
                              key={cat}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-stone-100 cursor-pointer text-stone-800"
                              data-testid={`summary-category-option-${cat}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSummaryCategory(cat)}
                                className="rounded"
                              />
                              <span>{cat}</span>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                {/* 2026-02 — All vs Tagged toggle. Tagged restricts the
                    summary to DCs whose LR doc is fully populated. */}
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">Scope</Label>
                  <div
                    className="inline-flex items-center bg-stone-100 rounded-md p-0.5 h-9 border border-stone-300"
                    data-testid="summary-tagged-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => setSummaryTaggedOnly(false)}
                      className={
                        'px-3 h-8 text-xs font-medium rounded transition-colors ' +
                        (!summaryTaggedOnly
                          ? 'bg-white text-amber-700 shadow-sm'
                          : 'text-stone-600 hover:text-stone-900')
                      }
                      data-testid="summary-scope-all"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSummaryTaggedOnly(true)}
                      className={
                        'px-3 h-8 text-xs font-medium rounded transition-colors flex items-center gap-1 ' +
                        (summaryTaggedOnly
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'text-stone-600 hover:text-stone-900')
                      }
                      data-testid="summary-scope-tagged"
                      title="Only count DCs with LR number, LR date, delivery date and freight charge populated"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Tagged
                    </button>
                  </div>
                </div>
                <Button
                  onClick={fetchSummary}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="summary-apply-btn"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Apply Filter
                </Button>
                {summaryTaggedOnly && (
                  <div className="ml-auto text-xs text-emerald-700 italic" data-testid="summary-tagged-hint">
                    Showing only DCs with LR details filled.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {summaryLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : summary ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-700 text-sm">Total Entries</p>
                        <p className="text-3xl font-bold text-stone-900">{summary.summary.total_entries}</p>
                      </div>
                      <FileText className="w-10 h-10 text-blue-700 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-700 text-sm">Total Boxes</p>
                        <p className="text-3xl font-bold text-stone-900">{summary.summary.total_boxes}</p>
                      </div>
                      <Box className="w-10 h-10 text-green-700 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-700 text-sm">Total Quantity</p>
                        <p className="text-3xl font-bold text-stone-900">{summary.summary.total_qty.toLocaleString()}</p>
                      </div>
                      <Package className="w-10 h-10 text-amber-700 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-700 text-sm">Total Weight</p>
                        <p className="text-3xl font-bold text-stone-900">{summary.summary.total_weight_kg} kg</p>
                      </div>
                      <Scale className="w-10 h-10 text-purple-700 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Date Wise Trend */}
              <Card className="bg-white border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    Date-wise Dispatch Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-2 px-3 text-stone-600">Date</th>
                          <th className="text-right py-2 px-3 text-stone-600">Entries</th>
                          <th className="text-right py-2 px-3 text-stone-600">Boxes</th>
                          <th className="text-right py-2 px-3 text-stone-600">Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.date_wise.slice(0, 15).map((item, idx) => (
                          <tr key={idx} className="border-b border-stone-200 hover:bg-stone-50">
                            <td className="py-2 px-3 text-stone-900">{item.date}</td>
                            <td className="py-2 px-3 text-right text-blue-700">{item.entries}</td>
                            <td className="py-2 px-3 text-right text-green-700">{item.boxes}</td>
                            <td className="py-2 px-3 text-right text-amber-700">{item.qty.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Transport Summary */}
              <Card className="bg-white border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="w-5 h-5 text-amber-600" />
                    Transport-wise Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {summary.transport_wise.slice(0, 9).map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-stone-50 rounded-lg p-3"
                        data-testid={`transport-summary-card-${idx}`}
                      >
                        <p className="font-medium text-stone-900 truncate">{item.transport_name || 'Unknown'}</p>
                        <div className="grid grid-cols-2 gap-1 mt-2 text-sm">
                          <span className="text-stone-600">Entries: <span className="text-blue-700">{item.entries}</span></span>
                          <span className="text-stone-600">Boxes: <span className="text-green-700">{item.boxes}</span></span>
                          <span className="text-stone-600">Qty: <span className="text-amber-700">{(item.qty || 0).toLocaleString()}</span></span>
                          <span
                            className="text-stone-600"
                            data-testid={`transport-summary-weight-${idx}`}
                          >
                            Weight: <span className="text-purple-700">{(item.weight_kg || 0).toLocaleString()} kg</span>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-12 text-stone-600">
              Select date range and click Apply Filter to view summary
            </div>
          )}
        </TabsContent>

        {/* All Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          {/* Filters */}
          <Card className="bg-white border-stone-200">
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-stone-600 text-sm">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-600" />
                    <Input
                      value={search}
                      onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                      placeholder="Gate pass, customer, transport..."
                      className="pl-10 bg-white border-stone-300"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">From</Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => { setFromDate(e.target.value); setPage(0); }}
                    className="bg-white border-stone-300 w-36"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">To</Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => { setToDate(e.target.value); setPage(0); }}
                    className="bg-white border-stone-300 w-36"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">Customer</Label>
                  <Select value={selectedCustomer} onValueChange={(v) => { setSelectedCustomer(v); setPage(0); }}>
                    <SelectTrigger className="bg-white border-stone-300 w-40">
                      <SelectValue placeholder="All Customers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-stone-600 text-sm">Status</Label>
                  <Select value={selectedStatus} onValueChange={(v) => { setSelectedStatus(v); setPage(0); }}>
                    <SelectTrigger className="bg-white border-stone-300 w-32">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="dispatched">Dispatched</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleExport} variant="outline" className="border-stone-300">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Entries Table */}
          <Card className="bg-white border-stone-200">
            <CardContent className="pt-4">
              {entriesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12 text-stone-600">
                  No entries found
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-200">
                          <th className="text-left py-3 px-3 text-stone-600">Gate Pass</th>
                          <th className="text-left py-3 px-3 text-stone-600">Date</th>
                          <th className="text-left py-3 px-3 text-stone-600">Transport</th>
                          <th className="text-left py-3 px-3 text-stone-600">Vehicle</th>
                          <th className="text-right py-3 px-3 text-stone-600">Boxes</th>
                          <th className="text-right py-3 px-3 text-stone-600">Qty</th>
                          <th className="text-right py-3 px-3 text-stone-600">Weight</th>
                          <th className="text-center py-3 px-3 text-stone-600">Status</th>
                          <th className="text-center py-3 px-3 text-stone-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry) => (
                          <tr 
                            key={entry.entry_id} 
                            className="border-b border-stone-200 hover:bg-stone-50 cursor-pointer"
                            onClick={() => fetchEntryDetail(entry.entry_id)}
                          >
                            <td className="py-3 px-3 font-mono text-amber-700">
                              {entry.gate_pass_number}
                            </td>
                            <td className="py-3 px-3 text-stone-700">
                              {formatDate(entry.created_at)}
                            </td>
                            <td className="py-3 px-3 text-stone-700">
                              {entry.transport_name || '-'}
                            </td>
                            <td className="py-3 px-3 text-stone-700">
                              {entry.vehicle_number || '-'}
                            </td>
                            <td className="py-3 px-3 text-right text-blue-700">
                              {entry.total_boxes || 0}
                            </td>
                            <td className="py-3 px-3 text-right text-green-700">
                              {(entry.total_qty || 0).toLocaleString()}
                            </td>
                            <td className="py-3 px-3 text-right text-purple-700">
                              {entry.total_weight_kg || 0} kg
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Badge className={getStatusBadge(entry.status)}>
                                {entry.status || 'Unknown'}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchEntryDetail(entry.entry_id);
                                  }}
                                  className="text-amber-700 hover:text-amber-900"
                                  data-testid={`view-entry-${entry.entry_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadGatePass(entry);
                                  }}
                                  className="text-green-700 hover:text-green-900"
                                  data-testid={`download-gatepass-${entry.entry_id}`}
                                  title="Download Gate Pass PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditDialog(entry.entry_id);
                                  }}
                                  className="text-blue-700 hover:text-blue-900"
                                  data-testid={`edit-entry-${entry.entry_id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-stone-200">
                    <p className="text-sm text-stone-600">
                      Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} entries
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                        className="border-stone-300"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-stone-600">
                        Page {page + 1} of {totalPages || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => p + 1)}
                        disabled={page >= totalPages - 1}
                        className="border-stone-300"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customer Wise Tab */}
        <TabsContent value="customer-wise" className="space-y-4">
          {summary && (
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-amber-600" />
                  Customer-wise Dispatch Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-left py-3 px-3 text-stone-600">Customer</th>
                        <th className="text-right py-3 px-3 text-stone-600">Entries</th>
                        <th className="text-right py-3 px-3 text-stone-600">Boxes</th>
                        <th className="text-right py-3 px-3 text-stone-600">Quantity</th>
                        <th className="text-right py-3 px-3 text-stone-600">Weight (kg)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.customer_wise.map((item, idx) => (
                        <tr key={idx} className="border-b border-stone-200 hover:bg-stone-50">
                          <td className="py-3 px-3 text-stone-900 font-medium">{item.customer}</td>
                          <td className="py-3 px-3 text-right text-blue-700">{item.entries}</td>
                          <td className="py-3 px-3 text-right text-green-700">{item.boxes}</td>
                          <td className="py-3 px-3 text-right text-amber-700">{item.qty.toLocaleString()}</td>
                          <td className="py-3 px-3 text-right text-purple-700">{item.weight_kg}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-300 bg-stone-50">
                        <td className="py-3 px-3 text-stone-900 font-bold">Total</td>
                        <td className="py-3 px-3 text-right text-blue-700 font-bold">
                          {summary.customer_wise.reduce((a, b) => a + b.entries, 0)}
                        </td>
                        <td className="py-3 px-3 text-right text-green-700 font-bold">
                          {summary.customer_wise.reduce((a, b) => a + b.boxes, 0)}
                        </td>
                        <td className="py-3 px-3 text-right text-amber-700 font-bold">
                          {summary.customer_wise.reduce((a, b) => a + b.qty, 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-3 text-right text-purple-700 font-bold">
                          {summary.customer_wise.reduce((a, b) => a + b.weight_kg, 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white border-stone-200">
          <DialogHeader>
            <DialogTitle className="text-xl text-amber-600">
              Gate Pass: {selectedEntry?.gate_pass_number}
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : selectedEntry && (
            <div className="space-y-4">
              {/* Entry Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-white rounded-lg">
                <div>
                  <p className="text-stone-600 text-sm">Customer</p>
                  <p className="text-stone-900 font-medium">{selectedEntry.customer}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Date</p>
                  <p className="text-stone-900">{formatDate(selectedEntry.created_at)}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Status</p>
                  <Badge className={getStatusBadge(selectedEntry.status)}>
                    {selectedEntry.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Transport</p>
                  <p className="text-stone-900">{selectedEntry.transport_name || '-'}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Vehicle No</p>
                  <p className="text-stone-900">{selectedEntry.vehicle_number || '-'}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Driver</p>
                  <p className="text-stone-900">{selectedEntry.driver_name || '-'}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Total Boxes</p>
                  <p className="text-blue-700 font-bold">{selectedEntry.total_boxes}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Total Qty</p>
                  <p className="text-green-700 font-bold">{selectedEntry.total_qty?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-stone-600 text-sm">Total Weight</p>
                  <p className="text-purple-700 font-bold">{selectedEntry.total_weight_kg} kg</p>
                </div>
              </div>

              {/* Boxes */}
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-3">Box Details</h3>
                <div className="space-y-3">
                  {selectedEntry.boxes?.map((box, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-stone-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-amber-700">{box.box_number}</span>
                        <div className="flex gap-4 text-sm">
                          <span className="text-stone-600">Qty: <span className="text-green-700">{box.qty}</span></span>
                          <span className="text-stone-600">Weight: <span className="text-purple-700">{box.weight_kg} kg</span></span>
                        </div>
                      </div>
                      {box.items && box.items.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-stone-200">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-stone-500">
                                <th className="text-left py-1">Design</th>
                                <th className="text-left py-1">Shade</th>
                                <th className="text-left py-1">Size</th>
                                <th className="text-right py-1">Qty</th>
                              </tr>
                            </thead>
                            <tbody>
                              {box.items.map((item, iidx) => (
                                <tr key={iidx} className="text-stone-700">
                                  <td className="py-1">{item.design}</td>
                                  <td className="py-1">{item.shade}</td>
                                  <td className="py-1">{item.size}</td>
                                  <td className="py-1 text-right text-green-700">{item.qty}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Remarks */}
              {selectedEntry.remarks && (
                <div className="p-3 bg-white rounded-lg">
                  <p className="text-stone-600 text-sm">Remarks</p>
                  <p className="text-stone-900">{selectedEntry.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={!!editEntry} onOpenChange={(open) => { if (!open) setEditEntry(null); }}>
        <DialogContent className="max-w-2xl bg-white border-stone-200 text-stone-900" data-testid="edit-entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Pencil className="w-4 h-4" /> Edit Gate Pass {editEntry?.gate_pass_number || ''}
            </DialogTitle>
          </DialogHeader>
          {editEntry && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-600">Transport</Label>
                  <Input
                    value={editForm.transport_name}
                    onChange={(e) => setEditForm(f => ({ ...f, transport_name: e.target.value }))}
                    className="bg-white border-stone-200 mt-1"
                    data-testid="edit-transport-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Vehicle Number</Label>
                  <Input
                    value={editForm.vehicle_number}
                    onChange={(e) => setEditForm(f => ({ ...f, vehicle_number: e.target.value }))}
                    className="bg-white border-stone-200 mt-1"
                    data-testid="edit-vehicle-number"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Driver Name</Label>
                  <Input
                    value={editForm.driver_name}
                    onChange={(e) => setEditForm(f => ({ ...f, driver_name: e.target.value }))}
                    className="bg-white border-stone-200 mt-1"
                    data-testid="edit-driver-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-600">Driver Phone</Label>
                  <Input
                    value={editForm.driver_phone}
                    onChange={(e) => setEditForm(f => ({ ...f, driver_phone: e.target.value }))}
                    className="bg-white border-stone-200 mt-1"
                    data-testid="edit-driver-phone"
                  />
                </div>
              </div>

              {editEntry.boxes && editEntry.boxes.length > 0 && (
                <div>
                  <Label className="text-xs text-stone-600">Per-Box Customer (fix wrongly-tagged boxes)</Label>
                  <div className="mt-2 space-y-2">
                    {editEntry.boxes.map((b) => (
                      <div key={b.box_number} className="flex items-center gap-2 bg-white rounded px-2 py-1.5 border border-stone-200">
                        <div className="text-xs font-mono text-amber-700 w-32 shrink-0 truncate">{b.box_number}</div>
                        <div className="text-[10px] text-stone-500 w-20 shrink-0 font-mono truncate">{b.pick_slip_number || ''}</div>
                        <Input
                          value={editForm.box_customers[b.box_number] ?? ''}
                          onChange={(e) => setEditForm(f => ({ ...f, box_customers: { ...f.box_customers, [b.box_number]: e.target.value } }))}
                          className="bg-white border-stone-200 h-8 text-sm flex-1"
                          placeholder="Customer name"
                          data-testid={`edit-box-customer-${b.box_number}`}
                        />
                        <div className="text-[10px] text-stone-500 w-10 text-right shrink-0">{b.total_qty || 0}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-stone-200">
                <Button variant="ghost" onClick={() => setEditEntry(null)} className="text-stone-700" data-testid="edit-cancel-btn">Cancel</Button>
                <Button onClick={saveEdit} disabled={savingEdit} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-save-btn">
                  {savingEdit ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CustomerOutwardReports;
